import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import styles from './BurnInDashboard.module.css';
import GoToDashboardButton from './GoToDashboardButton';
import BurnInDashboardSidebar from './burnIn/BurnInDashboardSidebar';
import BurnInDashboardWidgetBody from './burnIn/BurnInDashboardWidgets';
import DashboardDateFilter from './dashboard/DashboardDateFilter';
import DashboardCustomizeModal from './dashboard/DashboardCustomizeModal';
import {
  loadDashboardLayout,
  saveDashboardLayout,
} from './dashboard/dashboardLayouts';
import {
  getDefaultDateRange,
  isValidDateRange,
} from './dashboard/dashboardData';
import {
  burnInConfigs,
  burnInFeaturedNavItems,
  burnInNavItems,
} from './burnIn/BurnInAnalyticsPages';

const buildBurnInShortLabel = (title = '') =>
  title
    .replace(/^Burn-In\s+/i, '')
    .replace(/\s+Analysis$/i, '')
    .trim() || title;

const burnInDashboardChartItems = (() => {
  const orderedItems = [...burnInFeaturedNavItems, ...burnInNavItems];
  const seenPaths = new Set(orderedItems.map((item) => item.path));

  for (const [path, config] of Object.entries(burnInConfigs)) {
    if (seenPaths.has(path) || !config?.chartType) {
      continue;
    }

    orderedItems.push({
      path,
      label: config.title,
      shortLabel: buildBurnInShortLabel(config.title),
    });
    seenPaths.add(path);
  }

  return orderedItems.filter(
    (item, index, items) => items.findIndex((candidate) => candidate.path === item.path) === index
  );
})();

const getApiBase = () =>
  process.env.REACT_APP_API_BASE ||
  (window.location.port === '3000' ? 'http://localhost:3001' : '');

const formatDateParam = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  return value.toISOString().split('T')[0];
};

const staticWidgetDefs = [
  { id: 'upload_summary', defaultSize: 'normal' },
];

const analyticsWidgetDefs = burnInDashboardChartItems.map((item, index) => ({
  id: `burnin_widget_${index}`,
  defaultSize: item.path === '/burn-in-status' || item.path === '/burn-in-pie-chart' ? 'normal' : 'wide',
  defaultVisible: false,
  path: item.path,
}));

const widgetDefsBase = [...staticWidgetDefs, ...analyticsWidgetDefs];

export default function BurnInDashboard({ onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [layout, setLayout] = useState(() => loadDashboardLayout('smarti-burnin-dashboard-layout', widgetDefsBase));
  const [summary, setSummary] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(getDefaultDateRange);

  const appliedDateParams = useMemo(() => ({
    measuredDateStart: formatDateParam(appliedDateRange.start),
    measuredDateEnd: formatDateParam(appliedDateRange.end),
  }), [appliedDateRange.end, appliedDateRange.start]);

  useEffect(() => {
    saveDashboardLayout('smarti-burnin-dashboard-layout', layout);
  }, [layout]);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const response = await axios.get(`${getApiBase()}/api/burn-in/summary`, {
          withCredentials: true,
          params: appliedDateParams,
        });
        setSummary(response.data || null);
      } catch (error) {
        console.error('Failed to load burn-in summary:', error);
      }
    };

    loadSummary();
  }, [appliedDateParams]);

  const widgetDefs = useMemo(() => {
    const analyticsDefs = burnInDashboardChartItems.map((item, index) => ({
      id: `burnin_widget_${index}`,
      badge: t('Analytics'),
      title: t(item.label),
      description: t(`Open the dedicated ${item.shortLabel} burn-in view directly from the dashboard widget.`),
      path: item.path,
      configPath: item.path,
    }));

    return [
      {
        id: 'upload_summary',
        badge: t('Intake'),
        title: t('Burn-in upload summary'),
        description: t('Keep intake volume and source-file coverage visible.'),
      },
      ...analyticsDefs,
    ];
  }, [t]);

  const visibleWidgets = layout.filter((item) => item.visible);

  const handleStartDateChange = (date) => {
    setErrorMessage('');
    setDateRange((current) => ({ ...current, start: date }));
  };

  const handleEndDateChange = (date) => {
    setErrorMessage('');
    setDateRange((current) => ({ ...current, end: date }));
  };

  const applyDateFilter = () => {
    if (!isValidDateRange(dateRange.start, dateRange.end)) {
      setErrorMessage(t('Please choose a valid date range.'));
      return;
    }

    setErrorMessage('');
    setAppliedDateRange({
      start: dateRange.start,
      end: dateRange.end,
    });
  };

  const summaryContent = (
    <div className={styles.dateFilterSummaryControls}>
      <div className={styles.inlineSummaryPanel}>
        <div className={styles.inlineSummaryTile}>
          <span>{t('Uploads')}</span>
          <strong>{summary?.totals?.upload_count ?? 0}</strong>
        </div>
        <div className={styles.inlineSummaryTile}>
          <span>{t('Measurements')}</span>
          <strong>{summary?.totals?.measurement_count ?? 0}</strong>
        </div>
        <div className={styles.inlineSummaryTile}>
          <span>{t('Source files')}</span>
          <strong>{summary?.totals?.source_file_count ?? 0}</strong>
        </div>
        <div className={styles.inlineSummaryTile}>
          <span>{t('Unique units')}</span>
          <strong>{summary?.totals?.unit_count ?? 0}</strong>
        </div>
      </div>
      <button type="button" className={styles.customizeButton} onClick={() => setIsCustomizeOpen(true)}>
        {t('Customize dashboard')}
      </button>
    </div>
  );

  const renderWidget = (item) => {
    const widget = widgetDefs.find((entry) => entry.id === item.id);
    if (!widget) return null;

    const sizeClass = item.size === 'wide' ? styles.widgetWide : '';

    if (item.id === 'upload_summary') {
      return (
        <section key={item.id} className={`${styles.widgetCard} ${sizeClass}`}>
          <div className={styles.widgetHeader}>
            <div>
              <span className={styles.widgetBadge}>{widget.badge}</span>
              <h3>{widget.title}</h3>
              <p>{widget.description}</p>
            </div>
          </div>
          <div className={styles.metricStack}>
            <div className={styles.metricRow}>
              <span>{t('Uploads')}</span>
              <strong>{summary?.totals?.upload_count ?? 0}</strong>
            </div>
            <div className={styles.metricRow}>
              <span>{t('Measurements')}</span>
              <strong>{summary?.totals?.measurement_count ?? 0}</strong>
            </div>
            <div className={styles.metricRow}>
              <span>{t('Source files')}</span>
              <strong>{summary?.totals?.source_file_count ?? 0}</strong>
            </div>
            <div className={styles.metricRow}>
              <span>{t('Unique units')}</span>
              <strong>{summary?.totals?.unit_count ?? 0}</strong>
            </div>
          </div>
          <div className={styles.widgetLinkRow}>
            <button type="button" className={styles.widgetLinkButton} onClick={() => navigate('/burn-in-upload')}>
              {t('Open uploader')}
            </button>
          </div>
        </section>
      );
    }

    return (
      <section key={item.id} className={`${styles.widgetCard} ${sizeClass}`}>
        <div className={styles.widgetHeader}>
          <div>
            <span className={styles.widgetBadge}>{widget.badge}</span>
            <h3>{widget.title}</h3>
            <p>{widget.description}</p>
          </div>
        </div>
        <BurnInDashboardWidgetBody
          configPath={widget.configPath}
          styles={styles}
          navigate={navigate}
          path={widget.path}
          startDate={appliedDateRange.start}
          endDate={appliedDateRange.end}
        />
      </section>
    );
  };

  return (
    <div className={styles.container}>
      <BurnInDashboardSidebar
        isOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen((open) => !open)}
        onNavigate={navigate}
        onLogout={onLogout}
        styles={styles}
        t={t}
      />
      <GoToDashboardButton />

      <div className={`${styles.content} ${isSidebarOpen ? '' : styles.contentExpanded}`}>
        {!isSidebarOpen && (
          <button
            type="button"
            className={styles.floatingSidebarToggle}
            onClick={() => setIsSidebarOpen(true)}
          >
            {t('Show sidebar')}
          </button>
        )}
        <header className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>{t('Burn-in workspace')}</p>
            <h1 className={styles.title}>{t('Burn-In Dashboard')}</h1>
            <p className={styles.description}>
              {t('This separate dashboard keeps burn-in analysis clear and independent from the machine-data dashboard. Customize the widgets below so the team sees the burn-in charts that matter most first.')}
            </p>
          </div>
        </header>

        <DashboardDateFilter
          startDate={dateRange.start}
          endDate={dateRange.end}
          errorMessage={errorMessage}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onApply={applyDateFilter}
          summaryContent={summaryContent}
          styles={styles}
          t={t}
        />

        <section className={styles.widgetGrid}>
          {visibleWidgets.map((item) => renderWidget(item))}
        </section>
      </div>

      <DashboardCustomizeModal
        isOpen={isCustomizeOpen}
        onClose={() => setIsCustomizeOpen(false)}
        title={t('Arrange your burn-in widgets')}
        description={t('Show, hide, resize, and reorder your burn-in widgets in one focused modal so the dashboard itself stays clean and easy to scan.')}
        layout={layout}
        widgetDefs={widgetDefs}
        setLayout={setLayout}
        t={t}
        tone="burnin"
      />
    </div>
  );
}
